import { useInboxFlowRuns } from '@reflex/plugin-flows/web/useFlows';
import { useNotifications } from '@reflex/plugin-notifications/web/useNotifications';

/**
 * Inbox plugin source contribution contract.
 *
 * Host-owned shape the inbox aggregator (`useInboxCount`) reads from. The
 * shape is intentionally vendor-agnostic — it reports _counts_ only, so the
 * host never depends on a plugin's row/object shape to compute the inbox
 * badge. Aggregators apply their own caps and weightings; this seam never
 * encodes an inbox-specific policy.
 *
 * This module is the single, well-defined abstraction boundary between the
 * host's inbox and the plugins that currently contribute to it. It is the
 * only place in `web/` that imports `useInboxFlowRuns` (plugin-flows) and
 * `useNotifications` (plugin-notifications). Every other inbox consumer
 * reads from this seam instead of naming the plugins directly, so swapping
 * the implementation (a manifest-driven registry, host-owned `/inbox/*`
 * endpoints, …) only touches this file. Mirrors the host-owns-the-contract
 * pattern already used by `useDecisionGates`, which made the inbox neutral
 * to whichever producer opened a decision gate.
 *
 * Future direction: plugin-flows and plugin-notifications should register
 * their inbox source contributions through a host-owned extension point
 * (a `PluginWebManifest.inboxContributors` slot, mirroring `homeWidgets`),
 * and `useInboxPluginSources` should iterate the resolved contributors
 * instead of importing the two plugins by name. Until that surface lands,
 * this seam keeps the rest of the inbox code decoupled from vendor-specific
 * plugin modules.
 */
export interface InboxPluginCountSources {
  /**
   * Number of plugin-flows-contributed runs currently in an error state.
   * Uncapped — aggregators choose their own caps (the inbox caps completed
   * runs to 10, for example).
   */
  errorFlowCount: number;
  /**
   * Number of plugin-flows-contributed runs recently completed. Uncapped —
   * aggregators choose their own caps.
   */
  completedFlowCount: number;
  /**
   * Number of undismissed, non-expired notifications contributed by
   * plugin-notifications.
   */
  notificationCount: number;
}

/**
 * Host-owned seam over the plugin-contributed inbox count sources.
 *
 * Today this is a thin adapter over `plugin-flows` and `plugin-notifications`.
 * Tomorrow a registry / host-owned endpoint can replace the implementation
 * without touching any consumer — the contract above is what callers depend
 * on, not the plugin hooks beneath.
 */
export function useInboxPluginSources(): InboxPluginCountSources {
  const { data: inboxRuns } = useInboxFlowRuns();
  const { notifications } = useNotifications();
  return {
    errorFlowCount: inboxRuns?.errors.length ?? 0,
    completedFlowCount: inboxRuns?.recentCompleted.length ?? 0,
    notificationCount: notifications.length,
  };
}
